/* eslint-disable complexity */
import React from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';

import { ROUTES } from '@futrli/shared-services';
import { Forecast } from '@futrli-sdk/predict';

import { getDateMinusOneDay } from '../../../../utils/dates';
import styles from './predictions-nav-data-controls.scss';
import { ForecastingMenu } from './forecasts-menu';
import { CashFlowSwitch } from './cash-flow-switch';
import { PeriodsMenu } from './periods-menu';
import { NewAssumptionButton } from '../components/new-assumption-button';
import { ScenarioDatePicker } from './scenario-date-picker';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { DataGridScrollProvider } from '../data-grid-scroll-context';

interface Props {
  children?: React.ReactNode;
}

const showAddPredictionButton = (pathname: string) =>
  pathname.includes(ROUTES.SALES) ||
  pathname.includes(ROUTES.COSTS) ||
  pathname.includes(ROUTES.EXPENSES) ||
  pathname.includes(ROUTES.OTHER_P_AND_L) ||
  pathname.includes(ROUTES.PROFIT_AND_LOSS) ||
  pathname.includes(ROUTES.ASSETS) ||
  pathname.includes(ROUTES.LIABILITIES) ||
  pathname.includes(ROUTES.BALANCE_SHEET);

export const PredictionsNavDataControls = ({ children }: Props) => {
  const { forecast, isConsolidation, isReadOnly, updateForecast } = usePredictionsInfoContext();

  const { pathname } = useLocation();
  const { navHeight } = useNavHeight();

  const isInvoicesPage = pathname.indexOf(ROUTES.INVOICES) !== -1;

  const showScenarioDatePicker = forecast?.kind === 'scenario';

  const hideCashFlowSwitch =
    forecast?.kind === 'budget' ||
    pathname.indexOf(ROUTES.CASH_FLOW_STATEMENT) !== -1 ||
    pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;

  return (
    <div data-testid="predict-predictions-page">
      <DataGridScrollProvider>
        <nav className={clsx(styles.navigation, styles.newPageWidthWrapper)} style={{ top: navHeight }}>
          <ForecastingMenu />
          {!isInvoicesPage && (
            <>
              <div style={{ height: 34, borderRight: '1px solid #668494', marginRight: 18 }} />
              {hideCashFlowSwitch ? null : <CashFlowSwitch />}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {showScenarioDatePicker && !isReadOnly && (
                  <ScenarioDatePicker
                    forecastFromDate={forecast.forecast_from_date}
                    includeUnpaidInvoices={isConsolidation ? false : (forecast as Forecast).include_unpaid_invoices}
                    updateForecast={updateForecast}
                    windowFrom={forecast.window_from}
                    windowTill={getDateMinusOneDay(forecast.window_till)}
                  />
                )}
                <PeriodsMenu />
                {!isReadOnly && showAddPredictionButton(pathname) && <NewAssumptionButton />}
              </div>
            </>
          )}
        </nav>
        {children}
      </DataGridScrollProvider>
    </div>
  );
};
